import { postMutations, postQueries } from './index';
import { useQueryData } from '../apollo/query';
import { useMutationData } from '../apollo/mutation';
import {
  ICreatePostInput,
  IUpdatePostInput,
  IDeletePostInput,
  IPost,
  IGetPostInput,
  IListPostsInput,
  IQueryGetPostArgs,
  IQueryListPostsArgs,
  IMutationCreatePostArgs,
  IMutationUpdatePostArgs,
  IMutationDeletePostArgs,
} from 'types/types';

import { IError, ISuccess } from 'types/Models';
import { useTranslationText } from 'locale';

interface Callback {
  create: {
    loading: boolean;
    createPost: (input: ICreatePostInput) => Promise<{ id?: string; error?: IError; success?: ISuccess }>;
  };
  update: {
    loading: boolean;
    updatePost: (input: IUpdatePostInput) => Promise<{ error?: IError; success?: ISuccess }>;
  };
  delete: {
    loading: boolean;
    deletePost: (input: IDeletePostInput) => Promise<{ error?: IError; success?: ISuccess }>;
  };
  get: {
    error?: IError;
    loading: boolean;
    post?: IPost;
    getPost: (input: IGetPostInput) => void;
  };
  list: {
    loading: boolean | false;
    posts: IPost[];
    listPosts: (input: IListPostsInput) => void;
  };
  listPublic: {
    loading: boolean | false;
    posts: IPost[];
    listPostsPuclic: (input: IListPostsInput) => void;
  };
}

export const usePostApi = (): Callback => {
  const { getMessage } = useTranslationText();

  //MARK: GET QURERY
  const [getPost, { data: dataGet, error: errorGet, loading: getLoading }] = useQueryData<
    { getPost: IPost },
    IQueryGetPostArgs
  >({
    table: 'post',
    action: 'get',
    tData: 'getPost',
    fetchPolicy: 'cache-and-network',
    query: postQueries.GET,
  });

  //MARK: GET LIST
  const [listPosts, { data: dataList, loading: loadingList }] = useQueryData<
    { listPosts: IPost[] },
    IQueryListPostsArgs
  >({
    table: 'post',
    action: 'list',
    tData: 'listPosts',
    fetchPolicy: 'cache-and-network',
    query: postQueries.LIST,
  });

  const [listPostsPublic, { data: dataListPublic, loading: loadingListPublic }] = useQueryData<
    { listPostsPublic: IPost[] },
    IQueryListPostsArgs
  >({
    table: 'post',
    action: 'list',
    tData: 'listPostsPublic',
    fetchPolicy: 'cache-and-network',
    query: postQueries.LIST_PUBLIC,
  });

  //MARK: CREATE MUTATION
  const [createPost, { loading: createLoading }] = useMutationData<{ createPost: string }, IMutationCreatePostArgs>({
    mutation: postMutations.CREATE,
    table: 'post',
    action: 'add',
    tData: 'createPost',
  });

  //MARK: UPDATE MUTATION
  const [updatePost, { loading: updateLoading }] = useMutationData<{ updatePost: boolean }, IMutationUpdatePostArgs>({
    mutation: postMutations.UPDATE,
    table: 'post',
    action: 'update',
  });

  //MARK: DELETE MUTATION
  const [deletePost] = useMutationData<{ deletePost: boolean }, IMutationDeletePostArgs>({
    mutation: postMutations.DELETE,
    table: 'post',
    action: 'delete',
  });

  /********************** CUSTOM FUNCTION ************************* */

  //MARK:CREATE FUNCTION
  async function mutationcreatePost(
    news: ICreatePostInput,
  ): Promise<{ id?: string; error?: IError; success?: ISuccess }> {
    try {
      const create = await createPost({ variables: { input: news } });
      return { id: create.data?.createPost, success: getMessage('0', 'success') };
    } catch (error) {
      return { error: getMessage(error.message, 'error') };
    }
  }

  //MARK:UPDATE FUNCTION
  async function mutationupdatePost(news: IUpdatePostInput): Promise<{ error?: IError; success?: ISuccess }> {
    try {
      await updatePost({ variables: { input: news } });
      return { success: getMessage('0', 'success') };
    } catch (error) {
      return { error: getMessage(error.message, 'error') };
    }
  }

  //MARK:CREATE FUNCTION
  async function mutationdeletePost(deleteInput: IDeletePostInput): Promise<{ error?: IError; success?: ISuccess }> {
    try {
      await deletePost({ variables: { input: deleteInput } });
      return { success: getMessage('0', 'success') };
    } catch (error) {
      return { error: getMessage(error.message, 'error') };
    }
  }

  async function querygetPost(getInput: IGetPostInput) {
    getPost({ variables: { input: getInput } });
  }

  //MARK:GET FUNCTION
  function querylistPost(listIput: IListPostsInput) {
    listPosts({ variables: { input: listIput } });
  }

  function querylistPostPublic(listIput: IListPostsInput) {
    listPostsPublic({ variables: { input: listIput } });
  }

  return {
    create: {
      loading: createLoading,
      createPost: mutationcreatePost,
    },
    update: {
      loading: updateLoading,
      updatePost: mutationupdatePost,
    },

    delete: {
      loading: createLoading,
      deletePost: mutationdeletePost,
    },
    get: {
      error: errorGet ? getMessage(errorGet.message, 'error') : undefined,
      post: dataGet?.getPost,
      loading: dataGet?.getPost ? false : getLoading,
      getPost: querygetPost,
    },
    list: {
      loading: (dataList?.listPosts || []).length > 0 ? false : loadingList,
      posts: dataList?.listPosts || [],
      listPosts: querylistPost,
    },
    listPublic: {
      loading: (dataListPublic?.listPostsPublic || []).length > 0 ? false : loadingListPublic,
      posts: dataListPublic?.listPostsPublic || [],
      listPostsPuclic: querylistPostPublic,
    },
  };
};
